import { Typography } from 'antd';
import { formatAbsolute } from '@/lib/time';
import { erasedOr } from '@/lib/erased';

const { Text } = Typography;

/**
 * Renders one row from a freeform oversight array (SLA breach / recon break / recent review) whose
 * exact schema Track A owns. Best-effort field extraction: a headline + a timestamp + the remaining
 * scalar fields as a compact key/value strip. Never assumes a fixed schema, never crashes on a shape
 * it did not expect (the "render defensively" discipline).
 */

const HEADLINE_KEYS = ['detail', 'summary', 'title', 'description', 'reason', 'caseId', 'entityId', 'reviewId', 'id'];
const TIME_KEYS = ['at', 'breachedAt', 'reviewedAt', 'occurredAt', 'dueAt', 'completedAt', 'createdAt'];

function pickString(row: Record<string, unknown>, keys: string[]): { key: string; value: string } | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v.trim()) return { key: k, value: v };
    if (typeof v === 'number') return { key: k, value: String(v) };
  }
  return null;
}

export function DefensiveRow({ row }: { row: Record<string, unknown> }) {
  const headline = pickString(row, HEADLINE_KEYS);
  const time = pickString(row, TIME_KEYS);
  const shown = new Set<string>([headline?.key, time?.key].filter(Boolean) as string[]);
  const rest = Object.entries(row)
    .filter(([k, v]) => !shown.has(k) && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
    .slice(0, 6);

  return (
    <div
      style={{
        padding: '8px 10px',
        border: '1px solid var(--ol-border-subtle)',
        borderRadius: 8,
        background: 'var(--ol-bg-container)',
        marginBottom: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <Text strong style={{ fontSize: 12.5 }}>
          {headline ? erasedOr(headline.value) : 'Record'}
        </Text>
        {time && (
          <Text type="secondary" style={{ fontSize: 10.5, whiteSpace: 'nowrap' }}>
            {formatAbsolute(time.value)}
          </Text>
        )}
      </div>
      {rest.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginTop: 4 }}>
          {rest.map(([k, v]) => (
            <span key={k} style={{ fontSize: 11 }}>
              <span style={{ color: 'var(--ol-text-tertiary)' }}>{k}: </span>
              <span className="ol-mono" style={{ color: 'var(--ol-text-secondary)' }}>
                {String(erasedOr(v))}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
